import type { CatalogQueryParams } from "@/types/catalog";

interface BuilderInput {
  searchParamString: string;
  range: [number, number] | undefined;
  yearType: "gregorian" | "persian";
  limit?: number;
}

export interface CatalogConfig {
  baseParams: CatalogQueryParams;
  initialPage: number;
}

const SORT_MAP: Record<string, Pick<CatalogQueryParams, "sort_by" | "sort_order">> = {
  newest: { sort_by: "release_date", sort_order: "desc" },
  year_new: { sort_by: "release_date", sort_order: "desc" },
  year_old: { sort_by: "release_date", sort_order: "asc" },
  imdb: { sort_by: "imdb_rate", sort_order: "desc" },
};

function parseIdList(raw: string | null): string[] {
  if (!raw) return [];
  return raw
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean);
}

function mergeUniqueIds(...lists: string[][]): string[] {
  return Array.from(new Set(lists.flat()));
}


export function buildCatalogConfig({
  searchParamString,
  range,
  yearType,
  limit = 20,
}: BuilderInput): CatalogConfig {
  const params: CatalogQueryParams = { limit };
  const query = new URLSearchParams(searchParamString);

  const contentType = query.get("type");
  if (contentType === "movie" || contentType === "series") {
    params.content_type = contentType;
  }

  const searchValue = query.get("search");
  if (searchValue) params.search = searchValue;

  const genreIds = parseIdList(query.get("genres"));
  const countryIds = parseIdList(query.get("countries"));
  const tagIds = mergeUniqueIds(genreIds, countryIds);
  if (tagIds.length) params.tag_uuids = tagIds;

  const sortValue = query.get("sort");
  if (sortValue && SORT_MAP[sortValue]) {
    Object.assign(params, SORT_MAP[sortValue]);
  }

  if (range) {
    if (yearType === "gregorian") {
      params.date_from = `${range[0]}-01-01`;
      params.date_to = `${range[1]}-12-31`;
    } else {
      params.jalali_from = `${range[0]}-01-01`;
      params.jalali_to = `${range[1]}-12-29`;
    }
  }

  const pageValue = Number(query.get("page") || "1");
  const initialPage = !Number.isNaN(pageValue) && pageValue > 0 ? pageValue : 1;

  return { baseParams: params, initialPage };
}
